import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  FileText,
  Settings,
  BarChart3,
  Bell,
  User,
  X,
  Palette,
  Pen,
  Layers,
} from 'lucide-react';
import { authManager } from '../../services/authManager';
import LogoutButton from '../auth/LogoutButton';

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  submenu?: {
    name: string;
    href: string;
  }[];
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isDesktop: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, isDesktop }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['Élaboration de Document']));

  // Vérifier le rôle de l'utilisateur
  const user = authManager.getUser();

  // Récupérer le rôle principal (priorité au champ role du modèle User)
  const userRole = user?.role || user?.roles?.[0] || '';
  const userRoles = user?.roles || [];

  // Normaliser les rôles en minuscules pour la comparaison
  const normalizedRole = String(userRole || '').toLowerCase().trim();
  const normalizedRoles = userRoles.map((r: string) => String(r || '').toLowerCase().trim());

  // Debug: afficher les informations de rôle dans la console (uniquement en développement)
  if (user && process.env.NODE_ENV === 'development') {
    console.debug('Sidebar role context', {
      email: user.email,
      role: user.role,
      roles: user.roles,
      normalizedRole,
      normalizedRoles,
      hasRoleAdmin: authManager.hasRole('admin'),
      hasRoleManager: authManager.hasRole('manager'),
    });
  }

  // Vérifier si admin (plusieurs façons pour être sûr)
  const isAdmin =
    normalizedRole === 'admin' ||
    normalizedRoles.includes('admin') ||
    authManager.hasRole('admin');

  // Vérifier si manager
  const isManager =
    normalizedRole === 'manager' ||
    normalizedRoles.includes('manager') ||
    authManager.hasRole('manager');

  // Vérifier si user/creator/viewer
  const isUser =
    ['user', 'creator', 'viewer'].includes(normalizedRole) ||
    normalizedRoles.some((r: string) => ['user', 'creator', 'viewer'].includes(r));

  if (process.env.NODE_ENV === 'development') {
  }

  // Vérifier les capabilities réelles de l'utilisateur
  // Règles par rôle :
  // - admin : accès à tout par défaut
  // - manager : accès à tout sauf Paramètres
  // - user/creator/viewer : pas d'accès à Paramètres, Équipes & Projets, Intégrations Cloud

  // Documents, Rédaction, Content : tous les rôles peuvent y accéder
  const canViewDocuments = isAdmin || isManager || isUser || authManager.hasCapability('documents.view');
  const canUseRedaction = isAdmin || isManager || isUser || authManager.hasCapability('redaction.use');
  const canUseContent = isAdmin || isManager || isUser || authManager.hasCapability('content.use');

  // Paramètres : admin uniquement (pas manager, pas user)
  const canAccessSettings = isAdmin;

  const navigation: NavigationItem[] = useMemo(() => {
    const items: NavigationItem[] = [];

    // Gestion de Connaissances - visible si l'utilisateur peut voir les documents
    if (canViewDocuments) {
      items.push({
        name: 'Gestion de Connaissances',
        icon: FileText,
        href: '/workspace/documents-libre',
        submenu: [
          // { name: "Recherche d'information", href: '/workspace/documents' },
          // { name: 'Analyse approfondie', href: '/workspace/documents-libre' }
        ]
      });
    }

    // Élaboration de Document - visible si l'utilisateur peut utiliser la rédaction
    if (canUseRedaction) {
      items.push({
        name: 'Élaboration de Document',
        icon: Pen,
        href: '/workspace/redaction-libre',
        submenu: [
          // { name: 'Cadré et Guidé', href: '/workspace/redaction' },
          // { name: 'Libre Assisté', href: '/workspace/redaction-libre' }
        ]
      });
    }

    // Support de Com - visible si l'utilisateur peut créer des supports
    if (canUseContent) {
      items.push({ name: 'Support de Com', href: '/workspace/content', icon: Palette });
    }

    if (canUseContent) {
      items.push({ name: 'Quantification', href: '/workspace/quantification', icon: BarChart3 });
    }

    // Brainstorming - visible pour tous (pas de capability spécifique pour l'instant)
    items.push({ name: 'Brainstorming', href: '/workspace/usecases', icon: Layers });

    items.push({ name: 'Notifications', href: '/workspace/notifications', icon: Bell });
    items.push({ name: 'Profil', href: '/workspace/profile', icon: User });

    if (canAccessSettings) {
      items.push({ name: 'Paramètres', href: '/workspace/settings', icon: Settings });
    }

    return items;
  }, [canAccessSettings, canViewDocuments, canUseRedaction, canUseContent]);

  const toggleMenu = (menuName: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuName)) {
      newExpanded.delete(menuName);
    } else {
      newExpanded.add(menuName);
    }
    setExpandedMenus(newExpanded);
  };

  // La déconnexion est maintenant gérée par le LogoutButton

  return (
    <>
      {/* Overlay pour mobile */}
      {isOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:h-screen md:flex md:flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between h-20 px-6 border-b border-gray-200">
            <div className="flex items-center w-24 h-24">
              <img alt="omnia-eg Logo" className="max-w-full max-h-full object-contain" src="/logo.png" loading="eager" decoding="async" />
            </div>
            <button
              onClick={onToggle}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="mt-6 px-3 md:mt-8 md:flex-1 md:flex md:flex-col md:overflow-y-auto">
            <div className="space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const isParentActive = item.submenu?.some(sub => location.pathname === sub.href);
                const isExpanded = expandedMenus.has(item.name);

                // Si l'item a un submenu
                if (item.submenu) {
                  const isParentLinkActive = item.href ? location.pathname === item.href : false;
                  const isMenuActive = isParentActive || isParentLinkActive;

                  return (
                    <div key={item.name}>
                      <div className="flex items-center">
                        {item.href ? (
                          <Link
                            to={item.href}
                            className={`group flex flex-1 items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${isMenuActive
                                ? 'bg-primary-100 text-primary-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                          >
                            <item.icon
                              className={`mr-3 h-5 w-5 ${isMenuActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                                }`}
                            />
                            <span className="flex-1 text-left">{item.name}</span>
                          </Link>
                        ) : (
                          <button
                            onClick={() => toggleMenu(item.name)}
                            className={`w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${isMenuActive
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                        >
                          <item.icon
                            className={`mr-3 h-5 w-5 ${isMenuActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                              }`}
                          />
                          <span className="flex-1 text-left">{item.name}</span>
                        </button>
                      )}
                      {/* <button
                        type="button"
                        onClick={() => toggleMenu(item.name)}
                        className="ml-1 rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                        aria-label={`Basculer ${item.name}`}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button> */}
                    </div>
                    {isExpanded && (
                      <div className="ml-4 flex flex-col gap-1 border-l-2 border-gray-200 pl-2">
                        {item.submenu.map((subItem) => {
                          const isSubActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`group relative flex w-full items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${isSubActive
                                  ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                            >
                              <span className="w-2 h-2 rounded-full bg-gray-400 mr-3"></span>
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              // Item sans submenu
              return (
                <Link
                  key={item.name}
                  to={item.href!}
                  className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${isActive
                      ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 ${isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                  />
                  {item.name}
                </Link>
              );
            })}
          </div>

          {canAccessSettings && (
            <div className="mt-6 px-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <img
                  src="/sidebar.png"
                  alt="Illustration Omnia"
                  className="mx-auto h-24 w-24 object-contain opacity-80"
                  decoding="async"
                  loading="eager"
                />
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-200 md:mt-auto">
            <LogoutButton
              variant="ghost"
              size="sm"
              className="w-full justify-start text-gray-700 hover:text-gray-900"
            />
          </div>
        </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
